import React, { useEffect, useRef, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    createStyles,
    Grid,
    makeStyles,
    TextField,
    Theme,
    Typography
} from "@material-ui/core";
import { Add } from '@material-ui/icons';
import { connect, useStore } from "react-redux";
import { Store } from "redux";
import { useSnackbar } from 'notistack';
import { ApplicationState } from "../../redux/reducers";
import { avanzarSeccionGuiaWizard, IGuiaWizard } from "../../entities/guias/IGuiaWizard";
import { IRecoleccion } from "../../redux/types/recoleccionActionTypes";
import { ICliente } from "../../entities/catalogos/ICliente";
import { setGuiaWizard } from "../../redux/actions/guiaActions";
import OrigenInHouseGuia from "./OrigenInHouseGuia";
import DestinoGuia from "./DestinoGuia";
import NextButton from "../controls/NextButton";
import Help from "../help/Help";
import swal from 'sweetalert2';
import { traducirButton, traducirLabel, traducirMessage } from "../../blocks/traductor";
import {
    destinoGuiaCompleto,
    origenGuiaCompleto,
    validarServiciosDestinoCamposGuia,
    validarServiciosOrigenDestinoGuia
} from "./guiaServiciosOrigenDestinoHelpers";
import { destinoGuiaTieneModalidadEntrega } from "./guiaRecoleccionOrigenHelpers";
import { enums } from "../../enums/enums";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            boxShadow: "none"
        },
        actions: {
            width: "100%",
            textAlign: "right",
            paddingTop: "20px"
        },
        heading: {
            fontSize: "0.875rem",
            color: "#00653b",
            fontWeight: theme.typography.fontWeightBold,
            borderBottom: "2px solid #00653b"
        },
        headingAdicional: {
            fontSize: "0.875rem",
            color: "#00653b",
            fontWeight: theme.typography.fontWeightBold
        }
    }),
);

interface StateProps {
    guiaWizard: IGuiaWizard;
    guiaOrigen: IRecoleccion;
    guiaDestino: IRecoleccion;
    cliente: ICliente;
}

interface DispatchProps {
    setGuiaWizard: typeof setGuiaWizard;
}

type Props = StateProps & DispatchProps;

const OrigenDestinoGuia = ({ guiaWizard, guiaOrigen, guiaDestino, cliente, setGuiaWizard }: Props) => {
    const classes = useStyles();
    const store = useStore() as Store<ApplicationState>;
    const { enqueueSnackbar } = useSnackbar();
    const [expanded, setExpanded] = useState<string | false>('Origen');
    const [destinoSeccionTerminada, setDestinoSeccionTerminada] = useState(false);
    const origenCompleto = origenGuiaCompleto(guiaWizard, guiaOrigen);
    const destinoCompleto = destinoGuiaCompleto(guiaDestino);
    const destinoHabilitado = origenCompleto;
    const adicionalHabilitado = destinoSeccionTerminada;
    const prevOrigenCompleto = useRef(origenCompleto);

    useEffect(() => {
        if (!prevOrigenCompleto.current && origenCompleto) {
            setExpanded('Destino');
        }
        prevOrigenCompleto.current = origenCompleto;
    }, [origenCompleto]);

    useEffect(() => {
        if (destinoCompleto) {
            setDestinoSeccionTerminada(true);
        }
    }, []);

    useEffect(() => {
        if (!destinoCompleto) {
            setDestinoSeccionTerminada(false);
        }
    }, [destinoCompleto]);

    const handleChangePanel = (panel: string) => (_event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        if (panel === 'Destino' && !destinoHabilitado) {
            return;
        }
        if (panel === 'Adicional' && !adicionalHabilitado) {
            return;
        }
        setExpanded(isExpanded ? panel : false);
    };

    const handleObservaciones = (value: string) => {
        const limpio = value.replace(/[^a-zA-Z0-9 ]/g, "");
        setGuiaWizard({
            ...guiaWizard,
            observaciones: limpio
        });
    };

    const validarDestinoParaContinuar = (): boolean => {
        if (!destinoCompleto) {
            if (guiaDestino.tipoRecoleccionId !== enums.tipoRecoleccion.ocurre
                && guiaDestino.tipoRecoleccionId
                && !destinoGuiaTieneModalidadEntrega(guiaDestino)) {
                swal.fire({
                    titleText: traducirMessage(276),
                    text: traducirMessage(238),
                    type: 'error',
                });
            } else if (!guiaDestino.contacto) {
                enqueueSnackbar(traducirMessage(110), { variant: "error" });
            } else {
                swal.fire({
                    titleText: traducirMessage(276),
                    text: localStorage.getItem("idioma") === 'en-US'
                        ? 'Complete the destination before continuing.'
                        : 'Complete el destino antes de continuar.',
                    type: 'error',
                });
            }
            return false;
        }

        const errorServicios = validarServiciosOrigenDestinoGuia(
            guiaOrigen.serviciosAdicionales,
            guiaDestino.serviciosAdicionales,
            cliente
        );
        if (errorServicios) {
            enqueueSnackbar(traducirMessage(parseInt(errorServicios)), { variant: "error" });
            return false;
        }

        const errorCampos = validarServiciosDestinoCamposGuia(guiaDestino.serviciosAdicionales);
        if (errorCampos) {
            enqueueSnackbar(traducirMessage(parseInt(errorCampos)), { variant: "error" });
            return false;
        }

        return true;
    };

    const handleTerminarDestino = () => {
        if (!validarDestinoParaContinuar()) {
            return;
        }
        setDestinoSeccionTerminada(true);
        setExpanded('Adicional');
    };

    const puedeAvanzar = () => origenCompleto && destinoCompleto && destinoSeccionTerminada;

    const handleSiguiente = () => {
        if (!origenCompleto) {
            swal.fire({
                titleText: traducirMessage(276),
                text: traducirMessage(122),
                type: 'error',
            });
            setExpanded('Origen');
            return;
        }
        if (!destinoSeccionTerminada) {
            if (!validarDestinoParaContinuar()) {
                setExpanded('Destino');
                return;
            }
            setDestinoSeccionTerminada(true);
        }
        if (!puedeAvanzar()) {
            swal.fire({
                titleText: traducirMessage(276),
                text: localStorage.getItem("idioma") === 'en-US'
                    ? 'Complete all required sections before continuing.'
                    : 'Complete todas las secciones obligatorias antes de continuar.',
                type: 'error',
            });
            return;
        }

        const wizardActual = store.getState().guiaWizard;
        setGuiaWizard(avanzarSeccionGuiaWizard(wizardActual, 2));
    };

    return (
        <div className={classes.root}>
            <Accordion expanded={expanded === 'Origen'} onChange={handleChangePanel('Origen')}>
                <AccordionSummary expandIcon={<Add />} aria-controls="origen-guia-content" id="origen-guia-header">
                    <Typography component={'span'} className={classes.heading}>
                        {traducirLabel(262)}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={1}>
                        <Grid item sm={12}>
                            {expanded === 'Origen' && <OrigenInHouseGuia />}
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Accordion expanded={expanded === 'Destino'} onChange={handleChangePanel('Destino')}>
                <AccordionSummary
                    disabled={!destinoHabilitado}
                    expandIcon={<Add />}
                    aria-controls="destino-guia-content"
                    id="destino-guia-header"
                >
                    <Typography component={'span'} className={classes.heading}>
                        {traducirLabel(85)}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={1}>
                        <Grid item sm={12}>
                            {destinoHabilitado && (
                                <DestinoGuia onTerminarDestino={handleTerminarDestino} />
                            )}
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Accordion expanded={expanded === 'Adicional'} onChange={handleChangePanel('Adicional')}>
                <AccordionSummary
                    disabled={!adicionalHabilitado}
                    expandIcon={<Add />}
                    aria-controls="adicional-guia-content"
                    id="adicional-guia-header"
                >
                    <Typography component={'span'} className={classes.heading}>
                        {traducirLabel(4)}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                            <Help comments={traducirMessage(37)} customStyle={{ top: "-10px", right: "-15px" }} />
                        </Grid>
                        <Grid item sm={12}>
                            <Box mt={2} mb={1} className={classes.headingAdicional}>
                                {traducirMessage(27)}
                            </Box>
                        </Grid>
                        <Grid item sm={12}>
                            <TextField
                                fullWidth
                                id="guia-observaciones"
                                variant="outlined"
                                onChange={e => e.target.value.length <= 250 && handleObservaciones(e.target.value)}
                                rows={10}
                                value={guiaWizard.observaciones ?? ""}
                                multiline
                                inputProps={{ maxLength: 250 }}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Grid container className={classes.actions} style={{ backgroundColor: "white", padding: "10px" }}>
                <Grid item xs={12}>
                    <NextButton nextStep={handleSiguiente} disabled={!puedeAvanzar()} label={traducirButton(7)} />
                </Grid>
            </Grid>
        </div>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    guiaWizard: state.guiaWizard,
    guiaOrigen: state.guiaOrigen,
    guiaDestino: state.guiaDestino,
    cliente: state.cliente
});

const mapDispatchToProps = {
    setGuiaWizard
};

export default connect(mapStateToProps, mapDispatchToProps)(OrigenDestinoGuia);
